import React, { Fragment, memo, useMemo } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, number, object, oneOfType, shape, string } from 'prop-types'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { getVariationDetails } from './getVariationDetails'
import useVisibleCount from './useVisibleCount'

const valueClassName = index => classnames(
  'variation-info__value',
  index === 0 && 'variation-info__value--primary',
)

const renderValues = (details, itemProps = {}) => details.map(({ label, value, shortValue }, index) => (
  <Fragment key={label}>
    {index > 0 && <span className="variation-info__dot" aria-hidden="true" />}
    <span className={valueClassName(index)} {...itemProps}>{shortValue ?? value}</span>
  </Fragment>
))

function VariationInfo({ variation, isCompact = false }) {
  const t = useTranslation()
  const details = useMemo(() => getVariationDetails(variation, t), [variation, t])
  const lines = details.map(({ label, value }) => `${label}: ${value}`)
  const [rowRef, measureRef, visibleCount] = useVisibleCount(details.length, lines.join('|'))
  const hiddenCount = details.length - visibleCount

  if (details.length === 0) {
    return null
  }

  return (
    <>
      <DesktopOnlyTooltip
        id={`variation-info-${variation.id}`}
        content={lines.map(line => <span key={line} className="variation-info__line">{line}</span>)}
        place="top"
        containerClass="variation-info"
        className="variation-info__trigger"
        focusable
        ariaLabel={lines.join(', ')}
      >
        <span
          ref={rowRef}
          className={classnames('variation-info__row', isCompact && 'variation-info__row--compact', 'cgt-caption')}
        >
          {renderValues(details.slice(0, visibleCount))}
          {hiddenCount > 0 && (
            <>
              <span className="variation-info__dot" aria-hidden="true" />
              <span className="variation-info__more">+{hiddenCount}</span>
            </>
          )}
          <span ref={measureRef} className="variation-info__measure" aria-hidden="true">
            {renderValues(details, { 'data-measure-item': true })}
            <span className="variation-info__dot" />
            <span className="variation-info__more" data-measure-counter>+{details.length}</span>
          </span>
        </span>
      </DesktopOnlyTooltip>
      {/* Below sm there is no hover to reveal the tooltip, so the stylesheet swaps
          the row for the full list. */}
      <ul className={classnames('variation-info__list', isCompact && 'variation-info__list--compact', 'cgt-caption')}>
        {lines.map(line => <li key={line} className="variation-info__list-item">{line}</li>)}
      </ul>
    </>
  )
}

VariationInfo.propTypes = {
  variation: shape({
    id: oneOfType([string, number]).isRequired,
    providerModelId: string,
    providerRunAttributes: object,
  }).isRequired,
  isCompact: bool,
}

export default memo(VariationInfo)
